import Box from '@/common/components/Box'
import { cn } from '@/common/utils/cn'
import DirectiveEntryDiagnosticEntryCompleted from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted'
import DirectiveEntryDiagnosticEntriesAdvance from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Advance'
import DirectiveEntryDiagnosticEntriesApprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Approve'
import DirectiveEntryDiagnosticEntriesDisapprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Disapprove'
import DirectiveEntryDiagnosticEntriesRegress from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Entries/DiagnosticEntry/Regress'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingDirectiveEntryStatus } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { useMemo } from 'react'

const MaintenanceWorkflowForwardingDirectiveEntrySectionDiagnosticEntry = () => {
  const { forwarding, forwardingUpdatedAt } =
    useForwardingTabContext<MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry>()

  const isDiagnosticEntryPending = useMemo(() => {
    return (
      forwarding?.directive_entry?.status &&
      [
        MaintenanceForwardingDirectiveEntryStatus.AwaitingManualStart,
        MaintenanceForwardingDirectiveEntryStatus.InProgress,
        MaintenanceForwardingDirectiveEntryStatus.AwaitingClientReview,
      ].includes(forwarding?.directive_entry?.status)
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryCompleted = useMemo(() => {
    return (
      (forwarding?.directive_entry?.status &&
        ![
          MaintenanceForwardingDirectiveEntryStatus.Pending,
          MaintenanceForwardingDirectiveEntryStatus.AwaitingManualStart,
          MaintenanceForwardingDirectiveEntryStatus.InProgress,
        ].includes(forwarding?.directive_entry?.status)) ||
      (forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.InProgress &&
        forwarding?.directive_entry?.last_review?.state === 'disapproved')
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryAwaitingClientReview = useMemo(() => {
    return (
      forwarding?.directive_entry?.status &&
      [MaintenanceForwardingDirectiveEntryStatus.AwaitingClientReview].includes(forwarding?.directive_entry?.status)
    )
  }, [forwardingUpdatedAt])

  const isDiagnosticEntryDisapproved = useMemo(() => {
    return forwarding?.directive_entry?.last_review?.state === 'disapproved'
  }, [forwardingUpdatedAt])

  return (
    <Box
      className={cn(
        'p-4 flex flex-col gap-4',
        isDiagnosticEntryAwaitingClientReview && !isDiagnosticEntryDisapproved && 'bg-yellow-op-2',
      )}
    >
      <div className="flex items-center justify-between flex-wrap">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">Diagnóstico e orçamento</p>

          {isDiagnosticEntryPending && (
            <p className="text-warning text-xs">Aguardando análise do diagnóstico e orçamento</p>
          )}
        </div>

        {isDiagnosticEntryAwaitingClientReview && (
          <div className="flex items-center gap-3">
            {isDiagnosticEntryDisapproved ? (
              <>
                <DirectiveEntryDiagnosticEntriesApprove />

                <DirectiveEntryDiagnosticEntriesDisapprove
                  buttonText="Alterar revisão"
                  defaultDescription={forwarding.directive_entry.last_review?.description}
                />

                <DirectiveEntryDiagnosticEntriesRegress />
              </>
            ) : (
              <>
                <DirectiveEntryDiagnosticEntriesDisapprove />

                <DirectiveEntryDiagnosticEntriesAdvance />
              </>
            )}
          </div>
        )}
      </div>

      {forwarding?.directive_entry && isDiagnosticEntryCompleted && (
        <DirectiveEntryDiagnosticEntryCompleted
          diagnosticId={forwarding.directive_entry.id}
          lastReview={forwarding.directive_entry.last_review}
          processStartedAt={forwarding.directive_entry.process_started_at}
          completedAt={forwarding.directive_entry.completed_at}
          technicalResponse={forwarding.directive_entry.technical_response}
          deadLineToCompleteInDays={forwarding.directive_entry.deadline_to_complete_in_days}
          totalValue={forwarding.directive_entry.total_value}
        />
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingDirectiveEntrySectionDiagnosticEntry
